import React from 'react'
import { Box, Button, ListItem, keyframes } from '@mui/material'
import { useTranslation } from '../../shared/hooks/useIntractionTranslate'
import { SidebarBrand, SidebarNavigationItem } from '../../shared/types/navigation'
import { SIDEBAR_SECTION_ATTRIBUTE } from '../../shared/utils/sidebarNavigation'
import { AriaBrand, AriaBrandVariant } from '../../shared/components/AriaBrand'
import { INTERAX_COLORS, TYPOGRAPHY } from '../../constants/interaxColors'
import { LAYOUT_SPACING, COMPONENT_DIMENSIONS } from '../../constants/layout'
import { LiveBadge } from '../LiveBadge'
import { NewBadge } from '../NewBadge'
import { ActiveIndicator } from './ActiveIndicator'
import { ActiveGlow } from './ActiveGlow'

const soonBlink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`

const SoonBadge: React.FC = () => {
  const { t } = useTranslation()
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: '100px',
        backgroundColor: INTERAX_COLORS.sun20,
        border: `1px solid ${INTERAX_COLORS.sun40}`,
        flexShrink: 0
      }}
    >
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          backgroundColor: INTERAX_COLORS.sun,
          animation: `${soonBlink} 1s infinite ease-in-out`
        }}
      />
      <Box
        component="span"
        sx={{
          fontSize: '0.6rem',
          fontWeight: 800,
          letterSpacing: '0.06em',
          color: INTERAX_COLORS.sun
        }}
      >
        {t('common.soon')}
      </Box>
    </Box>
  )
}

/**
 * Props for SidebarMenuItem
 */
export interface SidebarMenuItemProps {
  item: SidebarNavigationItem
  isActive: boolean
  /** Show live activity badge (pulsing green dot) */
  showLiveBadge?: boolean
  /** Show "soon" blinking badge (activity starting within 5 minutes) */
  showSoonBadge?: boolean
  /** Show the green "New" badge (feedback on this page not read yet) */
  showNewBadge?: boolean
  notificationCount?: number
  onClick: (item: SidebarNavigationItem) => void
}

/**
 * SidebarMenuItem - Individual sidebar navigation item
 *
 * Renders a single menu item with:
 * - Icon and label
 * - Active/inactive states
 * - Text gradient for active state
 * - Active indicator bar
 * - Orange glow halo
 */
export const SidebarMenuItem: React.FC<SidebarMenuItemProps> = ({
  item,
  isActive,
  showLiveBadge = false,
  showSoonBadge = false,
  showNewBadge = false,
  notificationCount = 0,
  onClick
}) => {
  const { t } = useTranslation()
  const hasNotificationCount = notificationCount > 0

  return (
    <ListItem
      {...{ [SIDEBAR_SECTION_ATTRIBUTE]: item.id }}
      sx={{
        padding: 0,
        position: 'relative',
        height: isActive ? '34px' : '32px' // Active items slightly taller
      }}
    >
      <Button
        onClick={() => onClick(item)}
        disabled={item.disabled}
        disableRipple
        fullWidth
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: `${LAYOUT_SPACING.SIDEBAR_ITEM_PADDING_VERTICAL}px ${LAYOUT_SPACING.SIDEBAR_ITEM_PADDING_HORIZONTAL}px`,
          gap: `${LAYOUT_SPACING.SIDEBAR_ITEM_GAP}px`,
          textAlign: 'left',
          transition: 'all 0.2s ease-in-out',
          textTransform: 'none',
          minHeight: 'auto',
          height: '100%',
          isolation: isActive ? 'isolate' : 'auto',

          // Disabled state
          ...(item.disabled && {
            cursor: 'not-allowed',
            opacity: 0.5,
            color: INTERAX_COLORS.thunderGrey,
            backgroundColor: 'transparent',
            borderRadius: `${COMPONENT_DIMENSIONS.BORDER_RADIUS_SM}px`,
            '&:hover': {
              backgroundColor: 'transparent'
            }
          }),

          // Active state - Complex embossed effect from Figma
          ...(isActive && {
            backgroundColor: '#212629', // Darker background
            borderRadius: `${COMPONENT_DIMENSIONS.BORDER_RADIUS_MD}px`, // 8px
            boxShadow:
              '-1px -1px 1px rgba(0, 0, 0, 0.25), -1px 1px 1px rgba(0, 0, 0, 0.25), inset 0px 1px 1px rgba(255, 255, 255, 0.15), inset 0px 2px 30px rgba(255, 255, 255, 0.1)',
            '&:hover': {
              backgroundColor: '#212629'
            }
          }),

          // Inactive state
          ...(!item.disabled &&
            !isActive && {
              backgroundColor: 'transparent',
              borderRadius: `${COMPONENT_DIMENSIONS.BORDER_RADIUS_SM}px`, // 6px
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }
            })
        }}
      >
        {/* Icon */}
        <Box
          component={item.icon}
          sx={{
            width: `${COMPONENT_DIMENSIONS.ICON_SM}px`, // 16px from Figma
            height: `${COMPONENT_DIMENSIONS.ICON_SM}px`,
            flexShrink: 0,
            zIndex: isActive ? 0 : 'auto',
            color: item.disabled
              ? INTERAX_COLORS.thunderGrey
              : isActive
                ? INTERAX_COLORS.white
                : INTERAX_COLORS.thunderGrey,
            '& path': {
              stroke: item.disabled
                ? INTERAX_COLORS.thunderGrey
                : isActive
                  ? INTERAX_COLORS.white
                  : INTERAX_COLORS.thunderGrey,
              strokeWidth: '1.5px'
            }
          }}
        />

        {/* Label */}
        <Box
          component="span"
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            whiteSpace: 'nowrap', // Prevent text wrapping
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            zIndex: isActive ? 1 : 'auto',

            // Active state - Gradient text
            ...(isActive && {
              ...TYPOGRAPHY.menuNonSelected, // Keep regular weight as per Figma
              background: 'linear-gradient(180deg, #FFFFFF 0%, #A1B1BA 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }),
            // Inactive state - Non-selected typography
            ...(!isActive && {
              ...TYPOGRAPHY.menuNonSelected,
              color: INTERAX_COLORS.thunderGrey
            })
          }}
        >
          {item.brand === SidebarBrand.ARIA_COACH ? (
            <AriaBrand
              variant={AriaBrandVariant.COACH}
              badgeColor={isActive ? INTERAX_COLORS.white : INTERAX_COLORS.thunderGrey}
            />
          ) : (
            t(item.label)
          )}
        </Box>

        {/* Live activity badge */}
        {hasNotificationCount && (
          <Box
            component="span"
            sx={{
              minWidth: 18,
              height: 18,
              px: notificationCount > 9 ? 0.75 : 0.5,
              borderRadius: '999px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#E25555',
              color: INTERAX_COLORS.white,
              fontSize: '11px',
              fontWeight: 700,
              lineHeight: 1,
              flexShrink: 0
            }}
            aria-label={`${notificationCount} unread notifications`}
          >
            {notificationCount > 9 ? '9+' : notificationCount}
          </Box>
        )}
        {showLiveBadge && <LiveBadge size="small" />}
        {showSoonBadge && <SoonBadge />}
        {showNewBadge && <NewBadge />}
      </Button>

      {/* Active Indicator Bar and Glow */}
      {isActive && (
        <>
          <ActiveIndicator />
          <ActiveGlow />
        </>
      )}
    </ListItem>
  )
}
